import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  Tabs,
  TextFilter,
  Toggle,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import useAdminLocations from "@/hooks/admin/useAdminLocations";
import useAdminTransferLocations from "@/hooks/admin/useAdminTransferLocations";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  adminPageSizePreference,
} from "@/utils/adminTable";
import { formatTimestamp } from "@/utils/utils";

import hoverStyles from "../common/hoverRows.module.css";
import EmptyState from "../EmptyState";
import { AdminLocationRow, LocationCategory } from "./types";

const CATEGORY_BADGE_COLOR: Record<
  LocationCategory,
  "blue" | "grey" | "green" | "red"
> = {
  EVS: "blue",
  ISG: "green",
  Cloud: "red",
  Archive: "grey",
  Other: "grey",
};

const shortId = (id: string): string =>
  id.length > 8 ? `${id.slice(0, 8)}…` : id;

interface LocationsTableProps {
  rows: AdminLocationRow[];
  idColumnHeader: string;
  loading: boolean;
  onRefresh: () => void;
  onToggle: (id: string, enabled: boolean) => void;
  testId: string;
}

const LocationsTable: React.FC<LocationsTableProps> = ({
  rows,
  idColumnHeader,
  loading,
  onRefresh,
  onToggle,
  testId,
}) => {
  const enabledCount = useMemo(
    () => rows.filter((r) => r.enabled).length,
    [rows]
  );

  // Rows per page, adjustable via the table's Preferences. State, not a ref:
  // the value is rendered — it decides how many rows appear — so the
  // re-render on change is the point.
  const [preferences, setPreferences] = useState<{ pageSize: number }>({
    pageSize: ADMIN_DEFAULT_PAGE_SIZE,
  });

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(rows, {
      filtering: {
        empty: <EmptyState title="No locations available" />,
        noMatch: (
          <EmptyState
            title="No matches"
            action={
              <Button onClick={() => actions.setFiltering("")}>
                Clear filter
              </Button>
            }
          />
        ),
        filteringFunction: (row: AdminLocationRow, text: string) => {
          const q = text.toLowerCase();
          return (
            row.displayName.toLowerCase().includes(q) ||
            row.id.toLowerCase().includes(q)
          );
        },
      },
      pagination: { pageSize: preferences.pageSize },
      // Order by the location name a reader is scanning for, ascending, with
      // disabled and enabled locations interleaved by name.
      sorting: {
        defaultState: { sortingColumn: { sortingField: "displayName" } },
      },
    });

  const columnDefinitions = useMemo<
    TableProps<AdminLocationRow>["columnDefinitions"]
  >(
    () => [
      {
        id: "displayName",
        header: "Location name",
        sortingField: "displayName",
        cell: (row) => <Box variant="strong">{row.displayName}</Box>,
      },
      {
        id: "id",
        header: idColumnHeader,
        cell: (row) => (
          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
            <Box
              color="text-status-inactive"
              fontSize="body-s"
              fontWeight="normal"
            >
              {shortId(row.id)}
            </Box>
            <Button
              variant="inline-icon"
              iconName="copy"
              ariaLabel={`Copy id for ${row.displayName}`}
              onClick={() => {
                void navigator.clipboard?.writeText(row.id);
              }}
            />
          </SpaceBetween>
        ),
      },
      {
        id: "category",
        header: "Category",
        cell: (row) => (
          <Badge color={CATEGORY_BADGE_COLOR[row.category]}>
            {row.category}
          </Badge>
        ),
      },
      {
        id: "enabled",
        header: "Enabled",
        cell: (row) => (
          <Toggle
            checked={row.enabled}
            onChange={({ detail }) => onToggle(row.id, detail.checked)}
            ariaLabel={`Enabled: ${row.displayName}`}
          />
        ),
      },
      {
        id: "lastChangedBy",
        header: "Last changed by",
        cell: (row) =>
          row.lastChangedBy ? (
            <div data-testid={`last-changed-by-${row.id}`}>
              <SpaceBetween size="xxs">
                <Box>{row.lastChangedBy}</Box>
                {row.lastChangedAt && (
                  <Box color="text-status-inactive" fontSize="body-s">
                    {formatTimestamp(row.lastChangedAt)}
                  </Box>
                )}
              </SpaceBetween>
            </div>
          ) : (
            <div data-testid={`last-changed-by-${row.id}`}>
              <Box color="text-status-inactive">—</Box>
            </div>
          ),
      },
    ],
    [idColumnHeader, onToggle]
  );

  return (
    <div className={hoverStyles.hoverRows}>
      <Table
        {...collectionProps}
        data-testid={testId}
        variant="container"
        columnDefinitions={columnDefinitions}
        items={items}
        loading={loading}
        loadingText="Loading locations"
        trackBy="id"
        empty={<EmptyState title="No locations available" />}
        header={
          <Header
            counter={`(${rows.length})`}
            description={`${enabledCount} enabled · ${
              rows.length - enabledCount
            } disabled`}
            actions={
              <Button
                iconName="refresh"
                onClick={onRefresh}
                ariaLabel="Sync from Iconik"
              >
                Sync
              </Button>
            }
          >
            Locations
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder="Search by location name or ID"
            countText={
              filteredItemsCount !== undefined ? `${filteredItemsCount} matches` : ""
            }
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            pageSizePreference={adminPageSizePreference()}
            onConfirm={({ detail }) =>
              setPreferences({
                pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE,
              })
            }
          />
        }
      />
    </div>
  );
};

const ExportLocationsAdmin: React.FC = () => {
  const exportLocations = useAdminLocations();
  const transferLocations = useAdminTransferLocations();

  return (
    <Tabs
      tabs={[
        {
          id: "export",
          label: "Export Locations",
          content: (
            <LocationsTable
              rows={exportLocations.rows}
              idColumnHeader="Location ID"
              loading={exportLocations.loading}
              onRefresh={exportLocations.loadLocations}
              onToggle={exportLocations.setEnabled}
              testId="export-locations-table"
            />
          ),
        },
        {
          id: "transfer",
          label: "Transfer Locations",
          content: (
            <LocationsTable
              rows={transferLocations.rows}
              idColumnHeader="Storage ID"
              loading={transferLocations.loading}
              onRefresh={transferLocations.loadLocations}
              onToggle={transferLocations.setEnabled}
              testId="transfer-locations-table"
            />
          ),
        },
      ]}
    />
  );
};

export default ExportLocationsAdmin;
